import { useState, useEffect, useCallback } from "react";
import { crApi } from "../lib/crScope";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { ScrollArea } from "../components/ui/scroll-area";
import { toast } from "sonner";
import {
  Plus, Edit, Trash2, Settings, Layers, DollarSign,
  CheckCircle, Building2, Users, Save, RefreshCw, GripVertical,
  FileText, Phone, Mail, Upload, Loader2, Search, UserCheck
} from "lucide-react";

const CONFIG_TYPES = {
  ramos: { label: "Ramos", icon: Layers, description: "Tipos de ramo de pólizas" },
  monedas: { label: "Monedas", icon: DollarSign, description: "Monedas disponibles" },
  status: { label: "Status", icon: CheckCircle, description: "Estados de renovación" },
  clases: { label: "Clases de Cliente", icon: Users, description: "Clasificación de clientes" },
  sucursales: { label: "Sucursales", icon: Building2, description: "Sucursales de la empresa" },
  companias: { label: "Aseguradoras", icon: Building2, description: "Compañías aseguradoras (alimenta el campo Compañía de Cotizaciones)" },
  tipos_poliza: { label: "Tipo de Póliza", icon: FileText, description: "Tipos de póliza (Maestra, Fronting, etc.)" },
  lista_supervisores: { label: "Supervisor", icon: UserCheck, description: "Lista de supervisores para renovaciones" },
  gestores_cobros: { label: "Gestor de Cobros", icon: DollarSign, description: "Lista de gestores de cobros" },
  vendedores: { label: "Vendedor", icon: Users, description: "Lista de vendedores" },
  lineas: { label: "Línea", icon: Layers, description: "Líneas de negocio" },
  checklist_na: { label: "Checklist No Requeridos", icon: CheckCircle, description: "Ramos cuyas pólizas NO requieren checklist de coberturas" },
  contactos: { label: "Carta de Equipos", icon: FileText, description: "Contactos para carta de equipos" },
  supervisores: { label: "Supervisores/Vendedores", icon: UserCheck, description: "Gestión de supervisores y vendedores" }
};

// Subpestañas restringidas solo a administradores
const ADMIN_ONLY_TABS = ["contactos", "supervisores", "status"];

// Clasificaciones de tratamiento para los Status (conteos y tarjetas)
const CLASIFICACIONES_STATUS = [
  { id: "pendiente", label: "Pendiente", desc: "Cuenta como pendiente (no trabajada)" },
  { id: "recibida", label: "Recibida", desc: "Cuenta en Recibidas (no trabajada)" },
  { id: "en_cotizacion", label: "En Cotización", desc: "Cuenta en Cotización (trabajada)" },
  { id: "renovada", label: "Renovada", desc: "Cuenta en Renovadas del Mes (trabajada)" },
  { id: "no_renovada", label: "No Renovada / Cancelada", desc: "Cuenta en No Renovadas (trabajada)" },
  { id: "trabajada", label: "Trabajada (genérica)", desc: "Solo cuenta como trabajada" },
];

// Réplica del default del backend para status sin clasificación asignada
const clasifDefaultStatus = (nombre) => {
  const n = (nombre || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().toUpperCase();
  if (n === "PENDIENTE" || n === "EN PROCESO") return "pendiente";
  if (n === "RECIBIDA") return "recibida";
  if (n === "EN COTIZACION/LICITACION" || n === "EN COTIZACION") return "en_cotizacion";
  if (n === "RENOVADA") return "renovada";
  if (["CANCELADA X PAGO", "CANCELADA POR PAGO", "CANCELADA", "CANCELADA X CLIENTE", "CANCELADA POR CLIENTE", "DESCONTINUADA"].includes(n)) return "no_renovada";
  return "trabajada";
};
// Subpestañas visibles solo para administradores y supervisores
const ADMIN_SUPERVISOR_TABS = ["lista_supervisores", "gestores_cobros", "vendedores", "lineas"];

// Tipos de contacto para clasificación de ejecutivos
// Plantillas de carta: 1 y 2 (Riesgos Generales); 3 Local y 4 Internacional (Riesgos de Personas)
const PLANTILLA_LABELS = { 1: "Plantilla 1", 2: "Plantilla 2", 3: "Local", 4: "Internacional" };
const plantillaLabel = (n) => PLANTILLA_LABELS[n || 1] || `Plantilla ${n}`;
const esModuloPersonas = () => window.location.pathname.startsWith("/riesgos-personas");

const TIPOS_CONTACTO = [
  "Individual/Pymes",
  "Corporativo/Empresarial",
  "Salud Local",
  "Salud Internacional",
  "Local",
  "Internacional",
  "Cotizaciones"
];

export default function ConfiguracionRenovaciones({ API_URL, getAuthHeaders, canEdit, isAdmin = false, isSupervisor = false, onConfigChange }) {
  const [activeConfigTab, setActiveConfigTab] = useState("ramos");
  // Tipos de config visibles según rol:
  // - contactos / supervisores: solo admin
  // - lista_supervisores / gestores_cobros / vendedores / lineas: admin o supervisor
  const visibleConfigTypes = Object.fromEntries(
    Object.entries(CONFIG_TYPES).filter(([key]) => {
      if (ADMIN_ONLY_TABS.includes(key)) return isAdmin;
      if (ADMIN_SUPERVISOR_TABS.includes(key)) return isAdmin || isSupervisor;
      return true;
    })
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  // Checklist No Requeridos (por ramo)
  const [ramosDisponibles, setRamosDisponibles] = useState([]);
  const [ramosNoRequeridos, setRamosNoRequeridos] = useState([]);
  const [savingChecklistNA, setSavingChecklistNA] = useState(false);
  const normRamo = (s) => (s || "").trim().toUpperCase();
  const toggleRamoNA = (nombre) => {
    setRamosNoRequeridos(prev => prev.some(r => normRamo(r) === normRamo(nombre))
      ? prev.filter(r => normRamo(r) !== normRamo(nombre))
      : [...prev, nombre]);
  };
  const guardarChecklistNA = async () => {
    setSavingChecklistNA(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/control-renovaciones/checklist-no-requerido`, {
        method: "PUT",
        ...getAuthHeaders(),
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ ramos: ramosNoRequeridos }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || "Error al guardar");
      toast.success(`Guardado: ${data.ramos.length} ramo${data.ramos.length !== 1 ? "s" : ""} sin requerir checklist`);
      if (onConfigChange) onConfigChange();
    } catch (e) {
      toast.error(e.message || "Error al guardar");
    } finally {
      setSavingChecklistNA(false);
    }
  };
  
  // Dialog states
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  
  // Form state
  const [formData, setFormData] = useState({
    nombre: "",
    descripcion: "",
    activo: true,
    orden: 0,
    clasificacion: "trabajada"
  });

  // Contactos state (Carta de Equipos)
  const [contactos, setContactos] = useState([]);
  const [contactosDepartamentos, setContactosDepartamentos] = useState([]);
  const [showContactoDialog, setShowContactoDialog] = useState(false);
  const [editingContacto, setEditingContacto] = useState(null);
  const [importingContactos, setImportingContactos] = useState(false);
  const [contactosSearchTerm, setContactosSearchTerm] = useState("");
  const [contactosTipoFilter, setContactosTipoFilter] = useState(""); // "" = todos
  const [contactoForm, setContactoForm] = useState({
    nombre: "",
    posicion: "",
    telefono: "",
    movil: "",
    correo: "",
    departamento: "",
    plantilla: 1,
    plantilla_personas: 3,
    activo: true
  });

  // Supervisores/Vendedores state
  const [supervisores, setSupervisores] = useState([]);
  const [supervisoresSearchTerm, setSupervisoresSearchTerm] = useState("");
  const [showSupervisorDialog, setShowSupervisorDialog] = useState(false);
  const [editingSupervisor, setEditingSupervisor] = useState(null);
  const [supervisorForm, setSupervisorForm] = useState({
    nombre: "",
    posicion: "",
    telefono: "",
    movil: "",
    correo: "",
    departamento: "Supervisor",
    plantilla: 1,
    plantilla_personas: 3,
    activo: true
  });

  const fetchItems = useCallback(async (configType) => {
    if (configType === "checklist_na") {
      // Sección custom: ramos que no requieren checklist
      setLoading(true);
      try {
        const [ramosRes, naRes] = await Promise.all([
          fetch(`${API_URL}${crApi()}/config-renovaciones/ramos`, getAuthHeaders()),
          fetch(`${API_URL}${crApi()}/control-renovaciones/checklist-no-requerido`, getAuthHeaders()),
        ]);
        const ramosData = await ramosRes.json();
        const naData = await naRes.json();
        // Espejo de la subpestaña Ramos: solo ramos activos; la selección guardada
        // se depura automáticamente de ramos eliminados/desactivados.
        const ramosAct = (ramosData.items || []).filter(i => i.activo !== false).map(i => i.nombre);
        setRamosDisponibles(ramosAct);
        const actSet = new Set(ramosAct.map(n => (n || "").trim().toUpperCase()));
        setRamosNoRequeridos((naData.ramos || []).filter(r => actSet.has((r || "").trim().toUpperCase())));
      } catch (error) {
        toast.error("Error al cargar la configuración de checklist");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (configType === "contactos") {
      // Fetch contactos de equipo from the new route
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}${crApi()}/carta-equipos/contactos`, getAuthHeaders());
        const data = await res.json();
        setContactos(data.contactos || []);
        
        // Get departamentos de la PLATAFORMA (mismos que usa el sistema)
        const deptRes = await fetch(`${API_URL}/api/departamentos`, getAuthHeaders());
        const deptData = await deptRes.json();
        const deptNames = Array.isArray(deptData)
          ? deptData.map((d) => d.name).filter(Boolean)
          : (deptData.departamentos || []);
        setContactosDepartamentos(deptNames);
      } catch (error) {
        console.error("Error fetching contactos:", error);
        toast.error("Error al cargar contactos");
      } finally {
        setLoading(false);
      }
      return;
    }

    if (configType === "supervisores") {
      // Fetch supervisores y vendedores
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}${crApi()}/carta-equipos/socios-vendedores`, getAuthHeaders());
        const data = await res.json();
        setSupervisores(data.socios_vendedores || []);
      } catch (error) {
        console.error("Error fetching supervisores:", error);
        toast.error("Error al cargar supervisores/vendedores");
      } finally {
        setLoading(false);
      }
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/config-renovaciones/${configType}`, getAuthHeaders());
      const data = await res.json();
      setItems(data.items || []);
    } catch (error) {
      console.error("Error fetching config items:", error);
      toast.error("Error al cargar configuración");
    } finally {
      setLoading(false);
    }
  }, [API_URL, getAuthHeaders]);

  useEffect(() => {
    fetchItems(activeConfigTab);
  }, [activeConfigTab, fetchItems]);

  const handleCreate = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    try {
      const res = await fetch(`${API_URL}${crApi()}/config-renovaciones/${activeConfigTab}`, {
        method: "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success("Item creado exitosamente");
        setShowNewDialog(false);
        resetForm();
        fetchItems(activeConfigTab);
        // Notify parent to reload config
        if (onConfigChange) onConfigChange();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al crear item");
      }
    } catch (error) {
      toast.error("Error al crear item");
    }
  };

  const handleUpdate = async () => {
    if (!selectedItem || !formData.nombre.trim()) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/config-renovaciones/${activeConfigTab}/${selectedItem.id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        toast.success("Item actualizado");
        setShowEditDialog(false);
        resetForm();
        fetchItems(activeConfigTab);
        // Notify parent to reload config
        if (onConfigChange) onConfigChange();
      } else {
        toast.error("Error al actualizar");
      }
    } catch (error) {
      toast.error("Error al actualizar");
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`¿Eliminar "${item.nombre}"?`)) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/config-renovaciones/${activeConfigTab}/${item.id}`, {
        method: "DELETE",
        ...getAuthHeaders()
      });

      if (res.ok) {
        toast.success("Item eliminado");
        fetchItems(activeConfigTab);
        // Notify parent to reload config
        if (onConfigChange) onConfigChange();
      } else {
        toast.error("Error al eliminar");
      }
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  const handleToggleActive = async (item) => {
    try {
      const res = await fetch(`${API_URL}${crApi()}/config-renovaciones/${activeConfigTab}/${item.id}`, {
        method: "PUT",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify({ activo: !item.activo })
      });

      if (res.ok) {
        toast.success(item.activo ? "Item desactivado" : "Item activado");
        fetchItems(activeConfigTab);
      }
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  const resetForm = () => {
    setFormData({ nombre: "", descripcion: "", activo: true, orden: 0, clasificacion: "trabajada" });
    setSelectedItem(null);
  };

  const openEditDialog = (item) => {
    setSelectedItem(item);
    setFormData({
      nombre: item.nombre,
      descripcion: item.descripcion || "",
      activo: item.activo,
      orden: item.orden || 0,
      clasificacion: item.clasificacion || (activeConfigTab === "status" ? clasifDefaultStatus(item.nombre) : "")
    });
    setShowEditDialog(true);
  };

  // === CONTACTOS FUNCTIONS ===
  const resetContactoForm = () => {
    setContactoForm({
      nombre: "",
      posicion: "",
      telefono: "",
      movil: "",
      correo: "",
      departamento: "",
      tipo: "",
      plantilla: 1,
      plantilla_personas: 3,
      activo: true
    });
    setEditingContacto(null);
  };

  const openEditContacto = (contacto) => {
    setEditingContacto(contacto);
    setContactoForm({
      nombre: contacto.nombre || "",
      posicion: contacto.posicion || "",
      telefono: contacto.telefono || "",
      movil: contacto.movil || "",
      correo: contacto.correo || "",
      departamento: contacto.departamento || "",
      tipo: contacto.tipo || "",
      plantilla: contacto.plantilla || 1,
      plantilla_personas: contacto.plantilla_personas || 3,
      activo: contacto.activo !== false
    });
    setShowContactoDialog(true);
  };

  const handleSaveContacto = async () => {
    if (!contactoForm.nombre.trim() || !contactoForm.departamento) {
      toast.error("Nombre y departamento son requeridos");
      return;
    }

    try {
      const url = editingContacto 
        ? `${API_URL}${crApi()}/carta-equipos/contactos/${editingContacto.id}`
        : `${API_URL}${crApi()}/carta-equipos/contactos`;
      
      const res = await fetch(url, {
        method: editingContacto ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(contactoForm)
      });

      if (res.ok) {
        toast.success(editingContacto ? "Contacto actualizado" : "Contacto creado");
        setShowContactoDialog(false);
        resetContactoForm();
        fetchItems("contactos");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al guardar contacto");
      }
    } catch (error) {
      toast.error("Error al guardar contacto");
    }
  };

  const handleDeleteContacto = async (contactoId) => {
    if (!window.confirm("¿Está seguro de eliminar este contacto?")) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/carta-equipos/contactos/${contactoId}`, {
        method: "DELETE",
        headers: getAuthHeaders().headers
      });

      if (res.ok) {
        toast.success("Contacto eliminado");
        fetchItems("contactos");
      } else {
        toast.error("Error al eliminar contacto");
      }
    } catch (error) {
      toast.error("Error al eliminar contacto");
    }
  };

  const handleImportDefaultContactos = async () => {
    setImportingContactos(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/carta-equipos/import-defaults`, {
        method: "POST",
        headers: getAuthHeaders().headers
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        fetchItems("contactos");
      } else {
        toast.error("Error al importar contactos");
      }
    } catch (error) {
      toast.error("Error al importar contactos");
    } finally {
      setImportingContactos(false);
    }
  };

  const [syncingContactos, setSyncingContactos] = useState(false);
  const handleSyncFromUsers = async () => {
    setSyncingContactos(true);
    try {
      const res = await fetch(`${API_URL}${crApi()}/carta-equipos/sync-from-users`, {
        method: "POST",
        headers: getAuthHeaders().headers,
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      const { added = 0, activated = 0, deactivated = 0 } = data;
      if (added + activated + deactivated === 0) {
        toast.info("Ya estaba sincronizado: no hay cambios.");
      } else {
        toast.success(`Sync OK · +${added} agregados · ${activated} reactivados · ${deactivated} desactivados`);
      }
      fetchItems("contactos");
    } catch (e) {
      toast.error("Error al sincronizar con usuarios");
    } finally {
      setSyncingContactos(false);
    }
  };

  // === SUPERVISORES/VENDEDORES FUNCTIONS ===
  const resetSupervisorForm = () => {
    setSupervisorForm({
      nombre: "",
      posicion: "",
      telefono: "",
      movil: "",
      correo: "",
      departamento: "Supervisor",
      plantilla: 1,
      plantilla_personas: 3,
      activo: true
    });
    setEditingSupervisor(null);
  };

  const openEditSupervisor = (supervisor) => {
    setEditingSupervisor(supervisor);
    setSupervisorForm({
      nombre: supervisor.nombre || "",
      posicion: supervisor.posicion || "",
      telefono: supervisor.telefono || "",
      movil: supervisor.movil || "",
      correo: supervisor.correo || "",
      departamento: supervisor.departamento || "Supervisor",
      plantilla: supervisor.plantilla || 1,
      plantilla_personas: supervisor.plantilla_personas || 3,
      activo: supervisor.activo !== false
    });
    setShowSupervisorDialog(true);
  };

  const handleSaveSupervisor = async () => {
    if (!supervisorForm.nombre.trim() || !supervisorForm.departamento) {
      toast.error("Nombre y departamento son requeridos");
      return;
    }

    try {
      const url = editingSupervisor 
        ? `${API_URL}${crApi()}/carta-equipos/contactos/${editingSupervisor.id}`
        : `${API_URL}${crApi()}/carta-equipos/contactos`;
      
      const res = await fetch(url, {
        method: editingSupervisor ? "PUT" : "POST",
        headers: { ...getAuthHeaders().headers, "Content-Type": "application/json" },
        body: JSON.stringify(supervisorForm)
      });

      if (res.ok) {
        toast.success(editingSupervisor ? "Supervisor/Vendedor actualizado" : "Supervisor/Vendedor creado");
        setShowSupervisorDialog(false);
        resetSupervisorForm();
        fetchItems("supervisores");
      } else {
        const error = await res.json();
        toast.error(error.detail || "Error al guardar");
      }
    } catch (error) {
      toast.error("Error al guardar supervisor/vendedor");
    }
  };

  const handleDeleteSupervisor = async (supervisorId) => {
    if (!window.confirm("¿Está seguro de eliminar este supervisor/vendedor?")) return;

    try {
      const res = await fetch(`${API_URL}${crApi()}/carta-equipos/contactos/${supervisorId}`, {
        method: "DELETE",
        headers: getAuthHeaders().headers
      });

      if (res.ok) {
        toast.success("Supervisor/Vendedor eliminado");
        fetchItems("supervisores");
      } else {
        toast.error("Error al eliminar");
      }
    } catch (error) {
      toast.error("Error al eliminar supervisor/vendedor");
    }
  };

  // Filtered contactos based on search term + tipo filter
  const filteredContactos = contactos.filter((c) => {
    // Filtro por tipo exacto (case-insensitive). "" = todos.
    if (contactosTipoFilter) {
      const ct = (c.tipo || "").trim().toLowerCase();
      const ft = contactosTipoFilter.trim().toLowerCase();
      if (contactosTipoFilter === "__SIN_TIPO__") {
        if (ct) return false;
      } else if (ct !== ft) {
        return false;
      }
    }
    if (!contactosSearchTerm) return true;
    const q = contactosSearchTerm.toLowerCase();
    return (
      c.nombre?.toLowerCase().includes(q) ||
      c.posicion?.toLowerCase().includes(q) ||
      c.departamento?.toLowerCase().includes(q) ||
      c.correo?.toLowerCase().includes(q) ||
      c.tipo?.toLowerCase().includes(q)
    );
  });

  // Conteos por tipo (incluye "Sin tipo") para mostrarlos en el selector
  const contactosTipoCounts = contactos.reduce((acc, c) => {
    const t = (c.tipo || "").trim();
    if (!t) acc["__SIN_TIPO__"] = (acc["__SIN_TIPO__"] || 0) + 1;
    else acc[t] = (acc[t] || 0) + 1;
    return acc;
  }, {});

  // Filtered supervisores based on search term
  const filteredSupervisores = supervisores.filter((s) =>
    s.nombre?.toLowerCase().includes(supervisoresSearchTerm.toLowerCase()) ||
    s.posicion?.toLowerCase().includes(supervisoresSearchTerm.toLowerCase()) ||
    s.departamento?.toLowerCase().includes(supervisoresSearchTerm.toLowerCase()) ||
    s.correo?.toLowerCase().includes(supervisoresSearchTerm.toLowerCase())
  );

  const currentConfig = CONFIG_TYPES[activeConfigTab];
  const IconComponent = currentConfig?.icon || Settings;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Configuración de Renovaciones
          </h2>
          <p className="text-sm text-slate-500">Gestiona los valores configurables del sistema</p>
        </div>
        <Button variant="outline" onClick={() => fetchItems(activeConfigTab)}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>

      {/* Config Type Tabs */}
      <Tabs value={activeConfigTab} onValueChange={setActiveConfigTab}>
        <TabsList className="w-full justify-start bg-white dark:bg-slate-800 border p-1 rounded-lg overflow-x-auto">
          {Object.entries(visibleConfigTypes).map(([key, config]) => {
            const Icon = config.icon;
            return (
              <TabsTrigger 
                key={key} 
                value={key}
                className="flex items-center gap-2 data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700 whitespace-nowrap"
              >
                <Icon className="w-4 h-4" />
                {config.label}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {Object.keys(visibleConfigTypes).filter(t => t !== "contactos" && t !== "supervisores" && t !== "checklist_na").map((configType) => (
          <TabsContent key={configType} value={configType} className="mt-4">
            <Card>
              <CardHeader className="pb-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <IconComponent className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{CONFIG_TYPES[configType].label}</CardTitle>
                      <p className="text-sm text-slate-500">{CONFIG_TYPES[configType].description}</p>
                    </div>
                  </div>
                  {canEdit && (
                    <Button onClick={() => { resetForm(); setShowNewDialog(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo {CONFIG_TYPES[configType].label.slice(0, -1) || "Item"}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : items.length === 0 ? (
                  <div className="text-center py-8 text-slate-500">
                    <IconComponent className="w-10 h-10 mx-auto mb-2 opacity-30" />
                    <p>No hay items configurados</p>
                    {canEdit && (
                      <Button className="mt-4" onClick={() => { resetForm(); setShowNewDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Agregar Primer Item
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-2">
                    {items.map((item, idx) => (
                      <div 
                        key={item.id} 
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          item.activo ? 'bg-white dark:bg-slate-800' : 'bg-slate-100 dark:bg-slate-900 opacity-60'
                        } hover:shadow-sm transition-shadow`}
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-slate-300" />
                          <span className="text-sm text-slate-400 w-6">{idx + 1}.</span>
                          <div>
                            <span className="font-medium">{item.nombre}</span>
                            {activeConfigTab === "status" && (
                              <Badge variant="outline" className="ml-2 text-[10px] text-sky-700 border-sky-300" data-testid={`badge-clasif-${item.id}`}>
                                {CLASIFICACIONES_STATUS.find(c => c.id === (item.clasificacion || clasifDefaultStatus(item.nombre)))?.label || "Trabajada (genérica)"}{!item.clasificacion ? " (auto)" : ""}
                              </Badge>
                            )}
                            {item.descripcion && (
                              <p className="text-xs text-slate-500">{item.descripcion}</p>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge 
                            className={item.activo ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-500"}
                          >
                            {item.activo ? "Activo" : "Inactivo"}
                          </Badge>
                          {canEdit && (
                            <>
                              <Switch
                                checked={item.activo}
                                onCheckedChange={() => handleToggleActive(item)}
                              />
                              <Button size="icon" variant="ghost" onClick={() => openEditDialog(item)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button 
                                size="icon" 
                                variant="ghost" 
                                className="text-red-500"
                                onClick={() => handleDelete(item)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        ))}

        {/* Checklist No Requeridos Tab */}
        <TabsContent value="checklist_na" className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-amber-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Checklist No Requeridos</CardTitle>
                    <p className="text-sm text-slate-500">Marca los ramos cuyas pólizas NO requieren checklist. En la grilla mostrarán el badge "Checklist No Requerido" y se contarán en la tarjeta "Checklist N/A" (no en "Sin Checklist").</p>
                  </div>
                </div>
                {canEdit && (
                  <Button onClick={guardarChecklistNA} disabled={savingChecklistNA} data-testid="checklist-na-save-btn">
                    {savingChecklistNA ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                    Guardar
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              ) : ramosDisponibles.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Layers className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hay ramos configurados. Agrega ramos en la pestaña "Ramos".</p>
                </div>
              ) : (
                <>
                  <p className="text-xs text-slate-500 mb-3" data-testid="checklist-na-contador">
                    {ramosNoRequeridos.length} ramo{ramosNoRequeridos.length !== 1 ? "s" : ""} marcado{ramosNoRequeridos.length !== 1 ? "s" : ""} como "no requiere checklist"
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {ramosDisponibles.map((nombre) => {
                      const marcado = ramosNoRequeridos.includes(nombre);
                      return (
                        <label
                          key={nombre}
                          className={`flex items-center gap-2 border rounded-lg px-3 py-2 cursor-pointer transition-colors ${marcado ? "border-amber-400 bg-amber-50 dark:bg-amber-900/20" : "border-slate-200 hover:border-amber-300"} ${!canEdit ? "opacity-60 pointer-events-none" : ""}`}
                          data-testid={`checklist-na-ramo-${nombre}`}
                        >
                          <input
                            type="checkbox"
                            checked={marcado}
                            onChange={() => toggleRamoNA(nombre)}
                            className="accent-amber-600"
                            disabled={!canEdit}
                          />
                          <span className="text-sm">{nombre}</span>
                        </label>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Carta de Equipos Tab */}
        <TabsContent value="contactos" className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <FileText className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Carta de Equipos</CardTitle>
                    <p className="text-sm text-slate-500">Contactos del equipo para generar cartas PDF</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {canEdit && (
                    <>
                      <Button
                        variant="outline"
                        onClick={handleSyncFromUsers}
                        disabled={syncingContactos}
                        title="Sincroniza la lista con los usuarios activos del sistema. Agrega los nuevos y desactiva los que ya no estén."
                        data-testid="btn-sync-from-users"
                      >
                        {syncingContactos ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <RefreshCw className="w-4 h-4 mr-2" />
                        )}
                        Sincronizar usuarios
                      </Button>
                      <Button 
                        variant="outline" 
                        onClick={handleImportDefaultContactos}
                        disabled={importingContactos}
                      >
                        {importingContactos ? (
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4 mr-2" />
                        )}
                        Importar Defaults
                      </Button>
                      <Button onClick={() => { resetContactoForm(); setShowContactoDialog(true); }}>
                        <Plus className="w-4 h-4 mr-2" />
                        Nuevo Contacto
                      </Button>
                    </>
                  )}
                </div>
              </div>
              {/* Search field + filter by tipo for contactos */}
              <div className="mt-4 flex flex-col md:flex-row gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, posición, departamento o correo..."
                    value={contactosSearchTerm}
                    onChange={(e) => setContactosSearchTerm(e.target.value)}
                    className="pl-10"
                    data-testid="contactos-search-input"
                  />
                </div>
                <Select value={contactosTipoFilter || "__ALL__"} onValueChange={(v) => setContactosTipoFilter(v === "__ALL__" ? "" : v)}>
                  <SelectTrigger className="md:w-64" data-testid="contactos-tipo-filter">
                    <SelectValue placeholder="Filtrar por tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__ALL__">Todos los tipos ({contactos.length})</SelectItem>
                    {TIPOS_CONTACTO.map((t) => (
                      <SelectItem key={t} value={t}>{t} ({contactosTipoCounts[t] || 0})</SelectItem>
                    ))}
                    <SelectItem value="__SIN_TIPO__">Sin tipo ({contactosTipoCounts["__SIN_TIPO__"] || 0})</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Chips de acceso rápido por tipo */}
              <div className="mt-2 flex flex-wrap gap-1.5">
                <button
                  type="button"
                  onClick={() => setContactosTipoFilter("")}
                  className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${!contactosTipoFilter ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                  data-testid="chip-tipo-todos"
                >
                  Todos · {contactos.length}
                </button>
                {TIPOS_CONTACTO.map((t) => {
                  const count = contactosTipoCounts[t] || 0;
                  if (count === 0) return null;
                  const active = contactosTipoFilter === t;
                  return (
                    <button
                      key={t}
                      type="button"
                      onClick={() => setContactosTipoFilter(active ? "" : t)}
                      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                      data-testid={`chip-tipo-${t.replace(/[^a-zA-Z0-9]/g, "-").toLowerCase()}`}
                    >
                      {t} · {count}
                    </button>
                  );
                })}
                {(contactosTipoCounts["__SIN_TIPO__"] || 0) > 0 && (
                  <button
                    type="button"
                    onClick={() => setContactosTipoFilter(contactosTipoFilter === "__SIN_TIPO__" ? "" : "__SIN_TIPO__")}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${contactosTipoFilter === "__SIN_TIPO__" ? "bg-slate-600 text-white border-slate-600" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}
                    data-testid="chip-tipo-sin-tipo"
                  >
                    Sin tipo · {contactosTipoCounts["__SIN_TIPO__"]}
                  </button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : contactos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hay contactos configurados</p>
                  <p className="text-xs mt-1">Haga clic en "Importar Defaults" para cargar los contactos iniciales</p>
                </div>
              ) : filteredContactos.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>
                    No se encontraron contactos
                    {contactosSearchTerm ? ` con "${contactosSearchTerm}"` : ""}
                    {contactosTipoFilter ? ` de tipo "${contactosTipoFilter === "__SIN_TIPO__" ? "Sin tipo" : contactosTipoFilter}"` : ""}
                  </p>
                  <Button variant="link" onClick={() => { setContactosSearchTerm(""); setContactosTipoFilter(""); }}>
                    Limpiar filtros
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="text-xs text-slate-500 mb-2">
                    Mostrando {filteredContactos.length} de {contactos.length} contactos
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Posición</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Móvil</TableHead>
                        <TableHead>Correo</TableHead>
                        <TableHead>Departamento</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Plantilla</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredContactos.map((contacto) => (
                        <TableRow key={contacto.id}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <span>{contacto.nombre}</span>
                              {contacto._source === "user_sync" && (
                                <span
                                  title="Sincronizado automáticamente desde un usuario del sistema"
                                  className="inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 border border-indigo-200"
                                  data-testid={`badge-auto-sync-${contacto.id}`}
                                >
                                  <RefreshCw className="w-2.5 h-2.5" />
                                  Auto
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-sm">{contacto.posicion}</TableCell>
                          <TableCell>
                            {contacto.telefono && (
                              <span className="text-sm text-slate-600">{contacto.telefono}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {contacto.movil && (
                              <span className="flex items-center gap-1 text-sm">
                                <Phone className="w-3 h-3" />
                                {contacto.movil}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {contacto.correo && (
                              <span className="flex items-center gap-1 text-sm">
                                <Mail className="w-3 h-3" />
                                {contacto.correo}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{contacto.departamento}</Badge>
                          </TableCell>
                          <TableCell>
                            {contacto.tipo ? (
                              <Badge variant="secondary" data-testid={`contacto-tipo-${contacto.id}`}>{contacto.tipo}</Badge>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {esModuloPersonas() ? (
                              <Badge variant={(contacto.plantilla_personas || 3) === 4 ? "secondary" : "default"} data-testid={`contacto-plantilla-${contacto.id}`}>
                                {plantillaLabel(contacto.plantilla_personas || 3)}
                              </Badge>
                            ) : (
                              <Badge variant={contacto.plantilla === 2 ? "secondary" : "default"} data-testid={`contacto-plantilla-${contacto.id}`}>
                                Plantilla {contacto.plantilla || 1}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit && (
                              <div className="flex justify-end gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openEditContacto(contacto)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="text-red-500"
                                  onClick={() => handleDeleteContacto(contacto.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Supervisores/Vendedores Tab */}
        <TabsContent value="supervisores" className="mt-4">
          <Card>
            <CardHeader className="pb-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <UserCheck className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Supervisores y Vendedores</CardTitle>
                    <p className="text-sm text-slate-500">Gestión de supervisores y vendedores para la Carta de Equipos</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  {canEdit && (
                    <Button onClick={() => { resetSupervisorForm(); setShowSupervisorDialog(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Nuevo Supervisor/Vendedor
                    </Button>
                  )}
                </div>
              </div>
              {/* Search field for supervisores */}
              <div className="mt-4 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, posición, departamento o correo..."
                  value={supervisoresSearchTerm}
                  onChange={(e) => setSupervisoresSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="supervisores-search-input"
                />
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
                </div>
              ) : supervisores.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <UserCheck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No hay supervisores/vendedores configurados</p>
                  <p className="text-xs mt-1">Los supervisores y vendedores aparecen en la Carta de Equipos</p>
                  {canEdit && (
                    <Button className="mt-4" onClick={() => { resetSupervisorForm(); setShowSupervisorDialog(true); }}>
                      <Plus className="w-4 h-4 mr-2" />
                      Agregar Primer Supervisor/Vendedor
                    </Button>
                  )}
                </div>
              ) : filteredSupervisores.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No se encontraron supervisores/vendedores con "{supervisoresSearchTerm}"</p>
                  <Button variant="link" onClick={() => setSupervisoresSearchTerm("")}>
                    Limpiar búsqueda
                  </Button>
                </div>
              ) : (
                <ScrollArea className="h-[500px]">
                  <div className="text-xs text-slate-500 mb-2">
                    Mostrando {filteredSupervisores.length} de {supervisores.length} supervisores/vendedores
                  </div>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Nombre</TableHead>
                        <TableHead>Posición</TableHead>
                        <TableHead>Teléfono</TableHead>
                        <TableHead>Móvil</TableHead>
                        <TableHead>Correo</TableHead>
                        <TableHead>Departamento</TableHead>
                        <TableHead>Plantilla</TableHead>
                        <TableHead className="text-right">Acciones</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSupervisores.map((supervisor) => (
                        <TableRow key={supervisor.id}>
                          <TableCell className="font-medium">{supervisor.nombre}</TableCell>
                          <TableCell className="text-sm">{supervisor.posicion}</TableCell>
                          <TableCell>
                            {supervisor.telefono && (
                              <span className="text-sm text-slate-600">{supervisor.telefono}</span>
                            )}
                          </TableCell>
                          <TableCell>
                            {supervisor.movil && (
                              <span className="flex items-center gap-1 text-sm">
                                <Phone className="w-3 h-3" />
                                {supervisor.movil}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            {supervisor.correo && (
                              <span className="flex items-center gap-1 text-sm">
                                <Mail className="w-3 h-3" />
                                {supervisor.correo}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant="outline" 
                              className={supervisor.departamento === "Supervisor" ? "bg-blue-50 text-blue-700" : "bg-green-50 text-green-700"}
                            >
                              {supervisor.departamento}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {esModuloPersonas() ? (
                              <Badge variant={(supervisor.plantilla_personas || 3) === 4 ? "secondary" : "default"} data-testid={`supervisor-plantilla-${supervisor.id}`}>
                                {plantillaLabel(supervisor.plantilla_personas || 3)}
                              </Badge>
                            ) : (
                              <Badge variant={supervisor.plantilla === 2 ? "secondary" : "default"} data-testid={`supervisor-plantilla-${supervisor.id}`}>
                                Plantilla {supervisor.plantilla || 1}
                              </Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {canEdit && (
                              <div className="flex justify-end gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openEditSupervisor(supervisor)}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button 
                                  size="icon" 
                                  variant="ghost" 
                                  className="text-red-500"
                                  onClick={() => handleDeleteSupervisor(supervisor.id)}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* New Item Dialog */}
      <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nuevo {CONFIG_TYPES[activeConfigTab]?.label.slice(0, -1) || "Item"}</DialogTitle>
            <DialogDescription>Agregar un nuevo valor a la configuración</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre del item..."
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
                placeholder="Descripción opcional..."
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Activo</Label>
              <Switch
                checked={formData.activo}
                onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
              />
            </div>
            {activeConfigTab === "status" && (
              <div>
                <Label>Tratamiento en conteos *</Label>
                <Select
                  value={formData.clasificacion || "trabajada"}
                  onValueChange={(v) => setFormData({ ...formData, clasificacion: v })}
                >
                  <SelectTrigger data-testid="clasificacion-status-select-nuevo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLASIFICACIONES_STATUS.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label} — {c.desc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Define cómo se contará este estatus en las tarjetas y estadísticas.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewDialog(false)}>Cancelar</Button>
            <Button onClick={handleCreate}>
              <Save className="w-4 h-4 mr-2" />
              Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Item Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Editar {CONFIG_TYPES[activeConfigTab]?.label.slice(0, -1) || "Item"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
              />
            </div>
            <div>
              <Label>Descripción</Label>
              <Input
                value={formData.descripcion}
                onChange={(e) => setFormData({ ...formData, descripcion: e.target.value })}
              />
            </div>
            <div className="flex items-center justify-between">
              <Label>Activo</Label>
              <Switch
                checked={formData.activo}
                onCheckedChange={(checked) => setFormData({ ...formData, activo: checked })}
              />
            </div>
            {activeConfigTab === "status" && (
              <div>
                <Label>Tratamiento en conteos *</Label>
                <Select
                  value={formData.clasificacion || "trabajada"}
                  onValueChange={(v) => setFormData({ ...formData, clasificacion: v })}
                >
                  <SelectTrigger data-testid="clasificacion-status-select-editar"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CLASIFICACIONES_STATUS.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label} — {c.desc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">Define cómo se contará este estatus en las tarjetas y estadísticas.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancelar</Button>
            <Button onClick={handleUpdate}>
              <Save className="w-4 h-4 mr-2" />
              Guardar Cambios
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Contacto Dialog (Carta de Equipos) */}
      <Dialog open={showContactoDialog} onOpenChange={setShowContactoDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingContacto ? "Editar" : "Nuevo"} Contacto</DialogTitle>
            <DialogDescription>Complete la información del contacto del equipo</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={contactoForm.nombre}
                  onChange={(e) => setContactoForm({ ...contactoForm, nombre: e.target.value })}
                  placeholder="Nombre completo"
                />
              </div>
              <div className="col-span-2">
                <Label>Posición</Label>
                <Input
                  value={contactoForm.posicion}
                  onChange={(e) => setContactoForm({ ...contactoForm, posicion: e.target.value })}
                  placeholder="Ej: Ejecutivo de Cobros"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={contactoForm.telefono}
                  onChange={(e) => setContactoForm({ ...contactoForm, telefono: e.target.value })}
                  placeholder="Teléfono fijo"
                />
              </div>
              <div>
                <Label>Móvil</Label>
                <Input
                  value={contactoForm.movil}
                  onChange={(e) => setContactoForm({ ...contactoForm, movil: e.target.value })}
                  placeholder="Celular"
                />
              </div>
              <div className="col-span-2">
                <Label>Correo</Label>
                <Input
                  type="email"
                  value={contactoForm.correo}
                  onChange={(e) => setContactoForm({ ...contactoForm, correo: e.target.value })}
                  placeholder="email@qasesores.com.do"
                />
              </div>
              <div className="col-span-2">
                <Label>Departamento *</Label>
                <Select
                  value={contactoForm.departamento}
                  onValueChange={(v) => setContactoForm({ ...contactoForm, departamento: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    {(contactosDepartamentos.length > 0 ? contactosDepartamentos : [
                      "Cobros", "Cotizaciones", "Reclamaciones", "Riesgos Generales", "Supervisor", "Vendedor"
                    ]).map((dept) => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Tipo</Label>
                <Select
                  value={contactoForm.tipo}
                  onValueChange={(v) => setContactoForm({ ...contactoForm, tipo: v })}
                >
                  <SelectTrigger data-testid="contacto-tipo-select">
                    <SelectValue placeholder="Seleccionar tipo (opcional)" />
                  </SelectTrigger>
                  <SelectContent>
                    {TIPOS_CONTACTO.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Clasificación del ejecutivo para formularios condicionales
                </p>
              </div>
              <div>
                <Label>Plantilla</Label>
                {esModuloPersonas() ? (
                  <Select
                    value={String(contactoForm.plantilla_personas || 3)}
                    onValueChange={(v) => setContactoForm({ ...contactoForm, plantilla_personas: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3" data-testid="plantilla-local-option">Local</SelectItem>
                      <SelectItem value="4" data-testid="plantilla-internacional-option">Internacional</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Select
                    value={String(contactoForm.plantilla || 1)}
                    onValueChange={(v) => setContactoForm({ ...contactoForm, plantilla: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Plantilla 1 (con Rosell)</SelectItem>
                      <SelectItem value="2">Plantilla 2 (sin Rosell)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                <p className="text-xs text-slate-500 mt-1">
                  Define qué formato de carta se genera para este contacto
                </p>
              </div>
              <div className="flex items-center justify-between">
                <Label>Activo</Label>
                <Switch
                  checked={contactoForm.activo}
                  onCheckedChange={(checked) => setContactoForm({ ...contactoForm, activo: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowContactoDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveContacto}>
              <Save className="w-4 h-4 mr-2" />
              {editingContacto ? "Guardar Cambios" : "Crear Contacto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supervisor/Vendedor Dialog */}
      <Dialog open={showSupervisorDialog} onOpenChange={setShowSupervisorDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingSupervisor ? "Editar" : "Nuevo"} Supervisor/Vendedor</DialogTitle>
            <DialogDescription>Complete la información del supervisor o vendedor</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label>Nombre *</Label>
                <Input
                  value={supervisorForm.nombre}
                  onChange={(e) => setSupervisorForm({ ...supervisorForm, nombre: e.target.value })}
                  placeholder="Nombre completo"
                />
              </div>
              <div className="col-span-2">
                <Label>Posición</Label>
                <Input
                  value={supervisorForm.posicion}
                  onChange={(e) => setSupervisorForm({ ...supervisorForm, posicion: e.target.value })}
                  placeholder="Ej: Socio Director, Vendedor"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={supervisorForm.telefono}
                  onChange={(e) => setSupervisorForm({ ...supervisorForm, telefono: e.target.value })}
                  placeholder="Teléfono fijo"
                />
              </div>
              <div>
                <Label>Móvil</Label>
                <Input
                  value={supervisorForm.movil}
                  onChange={(e) => setSupervisorForm({ ...supervisorForm, movil: e.target.value })}
                  placeholder="Celular"
                />
              </div>
              <div className="col-span-2">
                <Label>Correo</Label>
                <Input
                  type="email"
                  value={supervisorForm.correo}
                  onChange={(e) => setSupervisorForm({ ...supervisorForm, correo: e.target.value })}
                  placeholder="email@qasesores.com.do"
                />
              </div>
              <div className="col-span-2">
                <Label>Departamento *</Label>
                <Select
                  value={supervisorForm.departamento}
                  onValueChange={(v) => setSupervisorForm({ ...supervisorForm, departamento: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Seleccionar departamento" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Supervisor">Supervisor</SelectItem>
                    <SelectItem value="Vendedor">Vendedor</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1">
                  Este departamento aparecerá como sección en la Carta de Equipos
                </p>
              </div>
              <div>
                <Label>Plantilla</Label>
                {esModuloPersonas() ? (
                  <Select
                    value={String(supervisorForm.plantilla_personas || 3)}
                    onValueChange={(v) => setSupervisorForm({ ...supervisorForm, plantilla_personas: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3" data-testid="sup-plantilla-local-option">Local</SelectItem>
                      <SelectItem value="4" data-testid="sup-plantilla-internacional-option">Internacional</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <Select
                    value={String(supervisorForm.plantilla || 1)}
                    onValueChange={(v) => setSupervisorForm({ ...supervisorForm, plantilla: parseInt(v) })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Seleccionar plantilla" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="1">Plantilla 1 (con Rosell)</SelectItem>
                      <SelectItem value="2">Plantilla 2 (sin Rosell)</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              </div>
              <div className="flex items-center justify-between">
                <Label>Activo</Label>
                <Switch
                  checked={supervisorForm.activo}
                  onCheckedChange={(checked) => setSupervisorForm({ ...supervisorForm, activo: checked })}
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSupervisorDialog(false)}>Cancelar</Button>
            <Button onClick={handleSaveSupervisor}>
              <Save className="w-4 h-4 mr-2" />
              {editingSupervisor ? "Guardar Cambios" : "Crear Supervisor/Vendedor"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
